import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, of, tap } from 'rxjs';

export interface MetaResponse {
	version: string;
	environment: string;
	read_only_market_data: boolean;
	webpush_public_key: string;
}

/**
 * MetaService — fetches /api/meta once at app boot to discover runtime
 * feature flags. Used by the read-only market data banner and to disable
 * mutating buttons in admin views when running against a read-only data
 * source (e.g. the `prod-data` dev mode).
 *
 * NOTE: /api/meta is mounted at the root, NOT under /api/v1.
 */
@Injectable({ providedIn: 'root' })
export class MetaService {
	private readonly http = inject(HttpClient);

	readonly meta = signal<MetaResponse | null>(null);

	readonly readOnlyMarketData = signal<boolean>(false);
	readonly environment = signal<string>('unknown');
	/** VAPID public key for web-push; empty when web-push is disabled server-side. */
	readonly webpushPublicKey = signal<string>('');

	load() {
		return this.http.get<MetaResponse>('/api/meta').pipe(
			tap((res) => {
				this.meta.set(res);
				this.readOnlyMarketData.set(res.read_only_market_data);
				this.environment.set(res.environment);
				this.webpushPublicKey.set(res.webpush_public_key || '');
			}),
			catchError((err) => {
				console.warn('[MetaService] Failed to load /api/meta', err);
				return of(null);
			}),
		);
	}
}
